import type { ComponentProps } from 'react';

import Link from '@vs-shop/image-link/Link';

import { clsxm } from '@vs-shop/utils';
import { cva, type VariantProps } from 'class-variance-authority';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

/**
 * The A–Z index rail: one tile per initial, each a link into the listing filed
 * under it. Flat named parts a tenant arranges in its own JSX, each painting
 * itself.
 *
 *   <AlphabetRail aria-label={label}>
 *     {tiles.map((letter) => (
 *       <AlphabetRailItem key={letter}>
 *         <AlphabetRailLetter href={hrefFor(letter)} state={stateOf(letter)}>
 *           {letter}
 *         </AlphabetRailLetter>
 *       </AlphabetRailItem>
 *     ))}
 *     <AlphabetRailItem variant={AlphabetRailItemVariant.RESET}>
 *       <AlphabetRailReset href={clearHref} state={…}>{allLabel}</AlphabetRailReset>
 *     </AlphabetRailItem>
 *   </AlphabetRail>
 *
 * **WHICH tiles the rail has is data, and it arrives as composed children.**
 * The alphabet a catalogue reports is a backend answer — a Czech glossary files
 * terms under `Č` and `Ž`, an ASCII brand index does not — so the ordering, the
 * widening and the availability lookup all stay tenant-side. The rail
 * contributes the row, the tile's three appearances, and the two elements a
 * tile can be.
 *
 * **The rail states no size.** Its two callers in this repo are two different
 * sizes — a brand index packs 36 tiles of 40px, a glossary holds its tiles to a
 * 44px touch target — and both are shipped pages whose geometry is fixed. A
 * size is not an appearance (spec § "`variant` is an appearance axis, never a
 * size one"), so the recipe carries what the two agree on and each caller
 * states its own box through `className`. What that costs is one class list per
 * call site; what it buys is that neither page can be resized by an edit to the
 * other.
 *
 * **No atom and no state setter crosses.** Which letter is selected and what
 * clicking one does are the tenant's: the rail takes the selection as an
 * appearance and emits links, so a tenant holding it in `useState`, in a search
 * param or in an atom composes the same parts.
 *
 * Server-first: every part is a plain server component. A rail whose tenant
 * never intercepts a click costs no client JavaScript at all.
 */

/**
 * What a tile SAYS about its letter — that it leads somewhere, that it leads
 * nowhere, or that it is where the shopper already is.
 *
 * Required and without `defaultVariants`, so no tile's appearance is decided by
 * an omission. Everything here is colour, ring and type; the box is the
 * caller's, for the reason above.
 */
export const alphabetRailLetterVariants = cva(
  [
    'flex w-full items-center justify-center rounded-xs text-xl uppercase',
    'ring-foreground/10 ring ring-inset',
    'tab:text-[40px] tab:ring-0',
  ],
  {
    variants: {
      state: {
        /** The catalogue holds terms under it; the tile leads to them. */
        available: 'text-foreground hover:text-primary/75',
        /** Nothing is filed under it, so the tile leads nowhere and says so. */
        unavailable: 'bg-foreground/5 text-foreground/50 tab:bg-transparent',
        /** The listing on screen is this letter's. */
        selected: 'text-primary ring-primary hover:text-primary',
      },
    },
  }
);

/**
 * The private spelling of each axis below, and the one every `satisfies` clause
 * in this file names.
 *
 * Naming the EXPORTED alias there instead would annotate a const with a type
 * that shares its identifier, and Storybook's react-docgen resolves such a type
 * reference back to the const it annotates — a cycle it follows until the stack
 * runs out, taking the dev server's whole module graph with it.
 */
type AlphabetRailLetterStateName = NonNullable<
  VariantProps<typeof alphabetRailLetterVariants>['state']
>;

export type AlphabetRailLetterState = AlphabetRailLetterStateName;

/**
 * The tile's appearance axis, derived from the recipe and bound to it by
 * `satisfies`, so the union and the recipe cannot drift.
 */
export const AlphabetRailLetterState = {
  /** The catalogue holds terms under it; the tile leads to them. */
  AVAILABLE: 'available',
  /** Nothing is filed under it, so the tile leads nowhere and says so. */
  UNAVAILABLE: 'unavailable',
  /** The listing on screen is this letter's. */
  SELECTED: 'selected',
} as const satisfies Record<string, AlphabetRailLetterStateName>;

/**
 * The row the tiles wrap in.
 *
 * A `<ul>` because it IS a list of routes into the catalogue, and the label
 * naming it is the tenant's — only a tenant knows whether its index is an
 * alphabet, a register or a glossary.
 */
const alphabetRailClasses = 'flex flex-wrap items-center gap-x-0.5 gap-y-1 tab:gap-x-3 tab:gap-y-3';

export type AlphabetRailProps = ComponentProps<'ul'>;

/** The rail itself. */
export const AlphabetRail = ({ className, ...props }: AlphabetRailProps) => (
  <ul data-slot="alphabet-rail" className={clsxm(alphabetRailClasses, className)} {...props} />
);

/**
 * One tile's cell.
 *
 * The axis is not a size: it is whether the cell holds one CHARACTER or a WORD.
 * A letter's cell is a track the row divides evenly and the caller caps; the
 * reset's cell is given the width its label needs, and steps onto its own line
 * once the row is wide enough to have one.
 */
export const alphabetRailItemVariants = cva('flex-1', {
  variants: {
    variant: {
      /** One character. How wide that is, is the caller's. */
      letter: '',
      /** The word that clears the filter, given the width its label needs. */
      reset: 'tab:mt-3 tab:block',
    },
  },
});

type AlphabetRailItemVariantName = NonNullable<
  VariantProps<typeof alphabetRailItemVariants>['variant']
>;

export type AlphabetRailItemVariant = AlphabetRailItemVariantName;

/** The cell's appearance axis, derived from the recipe and bound to it by `satisfies`. */
export const AlphabetRailItemVariant = {
  /** One character. How wide that is, is the caller's. */
  LETTER: 'letter',
  /** The word that clears the filter, given the width its label needs. */
  RESET: 'reset',
} as const satisfies Record<string, AlphabetRailItemVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AlphabetRailItemProps = ComponentProps<'li'> & {
  readonly variant: AlphabetRailItemVariant;
};

/** One tile's cell. */
export const AlphabetRailItem = ({ className, variant, ...props }: AlphabetRailItemProps) => (
  <li
    data-slot="alphabet-rail-item"
    className={clsxm(alphabetRailItemVariants({ variant }), className)}
    {...props}
  />
);

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AlphabetRailLetterProps = VsLinkProps & {
  readonly state: AlphabetRailLetterState;
};

/**
 * A letter, as the link into its listing.
 *
 * The dedicated link part rather than an anchor hand-wrapped at the call site:
 * whole-tile click is the same decision in every tenant. Which URL it points at
 * is the tenant's routing, and `aria-current` / `aria-disabled` ride the native
 * prop spread — the attributes a screen reader announces are the markup itself,
 * so the part takes no second prop that could disagree with them.
 *
 * It accepts the `unavailable` state, because whether a letter the catalogue
 * holds nothing under is still a link is the TENANT's call:
 * {@link AlphabetRailLetterPlaceholder} is the other answer, and a page that
 * already ships dead letters keeps them until someone decides otherwise.
 *
 * Renders through the `Link` seam, so a host that aliases the specifier keeps
 * client-side navigation and one that does not resolves a plain `<a>`.
 */
export const AlphabetRailLetter = ({ className, state, ...props }: AlphabetRailLetterProps) => (
  <Link
    data-slot="alphabet-rail-letter"
    className={clsxm(alphabetRailLetterVariants({ state }), className)}
    {...props}
  />
);

export type AlphabetRailLetterPlaceholderProps = ComponentProps<'span'>;

/**
 * A letter the catalogue holds nothing under, rendered as no link at all.
 *
 * The alternative to an `aria-disabled` anchor: it promises a listing that does
 * not exist and puts a dead stop in the tab order between every pair of letters
 * that work. It keeps the tile's appearance so the rail's rhythm survives, and
 * its box is the caller's, exactly as {@link AlphabetRailLetter}'s is — the two
 * have to be interchangeable in one cell.
 */
export const AlphabetRailLetterPlaceholder = ({
  className,
  ...props
}: AlphabetRailLetterPlaceholderProps) => (
  <span
    data-slot="alphabet-rail-letter"
    aria-disabled="true"
    className={clsxm(alphabetRailLetterVariants({ state: 'unavailable' }), className)}
    {...props}
  />
);

/**
 * The tile that clears the filter, painted as the word it is rather than as a
 * square.
 *
 * Its axis is the same question as a letter's — is the shopper already here —
 * but its arms are different classes, because the unselected reset is an ACTION
 * offered rather than a route not taken, and reads as one. Its box, like a
 * letter's, is the caller's.
 */
export const alphabetRailResetVariants = cva(
  'flex w-full items-center justify-center rounded-xs px-4 text-xl ring ring-inset',
  {
    variants: {
      state: {
        /** No letter is filtering, so this is where the shopper is. */
        selected: 'text-primary ring-primary',
        /** A letter is filtering; pressing this drops it. */
        available:
          'text-foreground ring-foreground/10 tab:text-destructive/80 tab:hover:text-destructive',
      },
    },
  }
);

type AlphabetRailResetStateName = NonNullable<
  VariantProps<typeof alphabetRailResetVariants>['state']
>;

export type AlphabetRailResetState = AlphabetRailResetStateName;

/** The reset's appearance axis, derived from the recipe and bound to it by `satisfies`. */
export const AlphabetRailResetState = {
  /** No letter is filtering, so this is where the shopper is. */
  SELECTED: 'selected',
  /** A letter is filtering; pressing this drops it. */
  AVAILABLE: 'available',
} as const satisfies Record<string, AlphabetRailResetStateName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AlphabetRailResetProps = VsLinkProps & {
  readonly state: AlphabetRailResetState;
};

/**
 * The "all" tile. What it SAYS is the tenant's — a word in the shopper's
 * language — and so is the URL that drops the filter.
 */
export const AlphabetRailReset = ({ className, state, ...props }: AlphabetRailResetProps) => (
  <Link
    data-slot="alphabet-rail-reset"
    className={clsxm(alphabetRailResetVariants({ state }), className)}
    {...props}
  />
);
